import { useState } from 'react';
import { useController } from 'react-hook-form';

import { NumberInput, NumberInputProps } from '@mantine/core';

import { FieldWithSuggestions } from './FieldWithSuggestions';

type VoltageDropFieldProps = Omit<NumberInputProps, 'value' | 'onChange'> & {
    name: string;
};

// The form value is a fraction (0.03), the user types and sees a percentage (3).
const VoltageDropField = ({ name, ...props }: VoltageDropFieldProps) => {
    const {
        field: { value, onChange, onBlur },
        fieldState: { error, isTouched },
    } = useController({ name });

    const [focused, setFocused] = useState(false);

    return (
        <FieldWithSuggestions suggestions={VoltageDropSuggestions} onChange={onChange}>
            <NumberInput
                suffix={focused ? undefined : '%'}
                min={0}
                max={100}
                clampBehavior="strict"
                allowNegative={false}
                decimalScale={2}
                hideControls
                {...props}
                value={typeof value === 'number' ? fractionToPercent(value) : ''}
                onChange={(percent) => onChange(typeof percent === 'number' ? percentToFraction(percent) : null)}
                onFocus={(event) => {
                    setFocused(true);
                    props.onFocus?.(event);
                }}
                onBlur={(event) => {
                    setFocused(false);
                    onBlur();
                    props.onBlur?.(event);
                }}
                error={isTouched && error?.message}
            />
        </FieldWithSuggestions>
    );
};

const VoltageDropSuggestions = [1, 2, 3, 4, 5].map((percent) => ({
    value: percent.toString(),
    internalValue: percentToFraction(percent),
    label: `${percent}%`,
}));

// Rounding strips float noise such as 0.07 * 100 = 7.000000000000001.
function fractionToPercent(fraction: number) {
    return Number((fraction * 100).toFixed(6));
}

function percentToFraction(percent: number) {
    return Number((percent / 100).toFixed(8));
}

export { VoltageDropField };
